import * as React from 'react';
import { Link as LinkComponent } from 'widgets-for-react';

export const stripUrlToPath = (link: { title: string; url: string }): string => {
  let a = document.createElement('a')
  a.href = link.url
  return a.pathname + a.search + (a.hash || "")
}

export const urlShareSamePathnameOrParts = (urlSource: string, urlTarget: string, subitem?: boolean): boolean => {
  if (isLinkExternal(urlTarget)) return false

  let a1 = document.createElement('a')
  a1.href = urlSource

  let a2 = document.createElement('a')
  a2.href = urlTarget

  let a1Parts = a1.pathname.split(/\//g).filter((p) => p && p != '')
  let a2Parts = a2.pathname.split(/\//g).filter((p) => p && p != '')

  if (a1Parts.length == 0 || a2Parts.length == 0) {
    return a1Parts.length == a2Parts.length
  }

  // If the current URL is smaller than the target URL and a subitem don't show active
  // This avoids all subitems becoming active when the parent page is shown
  // example; '/reuma' would activate also `/reuma/over-reuma' because they shared a part
  if (a1Parts.length < a2Parts.length && subitem == true) {
    return false
  }

  if (a1Parts.length > a2Parts.length) {
    let tmp = a1Parts
    a1Parts = a2Parts
    a2Parts = tmp
  }

  for (let index = 0; index < a1Parts.length; index++) {
    if (a1Parts[index] != a2Parts[index]) {
      return false
    }
  }
  return true
}

export const getPathFromUrl = (url: string): string => {
  let a = document.createElement('a')
  a.href = url
  return a.pathname
}

export const isLinkExternal = (link: string): boolean => {
  if (link && link != '' && !link.startsWith('/')) {
    let currentUrlHost = window.location.host

    if (
      (link.toLowerCase().includes('https://') || link.toLowerCase().includes('http://')) &&
      new URL(link).host != currentUrlHost
    ) {
      return true
    }

    return false
  } else {
    return false
  }
}

const isReumaUrl = (url: string): boolean => {
  if (!url.startsWith('http')) return false
  const { hostname } = new URL(url)
  return hostname === 'reuma.nl' || hostname.endsWith('.reuma.nl')
}

type TargetBlankProps = Pick<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'rel' | 'referrerPolicy'>

export const getTargetBlankProps = (url: string): TargetBlankProps => {
  if (!url.startsWith('http')) return { rel: 'noopener' }

  return isReumaUrl(url)
    ? { rel: 'noopener', referrerPolicy: 'strict-origin-when-cross-origin' }
    : { rel: 'noopener noreferrer' }
}


export const linkWrapper = (link: { title: string; url: string, anchor?: string }): Link => ({
  kind: 'link',
  isLinkExternal: isLinkExternal(link.url),
  target: isLinkExternal(link.url) ? '_blank' : '_self',
  title: link.title,
  url: isLinkExternal(link.url) ? link.url : stripUrlToPath(link),
  anchor: link.anchor
})

export const parseLinkProps = (link: { title: string; url: string }, description: string, key: number): LinkProps => ({
  kind: 'link',
  key: key.toString(),
  title: link.title,
  text: description ? description : '',
  isLinkExternal: isLinkExternal(link.url),
  target: isLinkExternal(link.url) ? '_blank' : '_self',
  url: isLinkExternal(link.url) ? link.url : stripUrlToPath(link)
})

export type LinkProps = Link & {
  key?: string
  text?: string
}

export type Link = {
  kind: 'link'
  isLinkExternal: boolean
  target: '_blank' | '_self'
  url: string
  title: string
  anchor?: string
}

export const getFileType = (link: string): string => {
  let splitted = link.split('.').pop()
  let checkedTwice: string = splitted != undefined ? splitted : ''
  let output: string = checkedTwice
  return output
}

export function link(
  link: Link,
  style: { classname: string },
  inner: JSX.Element | string,
  id: string,
  onClick?: () => void
): JSX.Element {
  return link.isLinkExternal || link.target === '_blank' ? (
    <a
      id={id}
      className={style.classname}
      target={link.target}
      {...(link.target === '_blank' ? getTargetBlankProps(link.url) : {})}
      href={`${link.url}${link.anchor ? `#${link.anchor}` : ''}`}
      onClick={onClick || undefined}
    >
      {inner}
    </a>
  ) : (
    <LinkComponent id={id} className={style.classname} to={`${link.url}${link.anchor ? `#${link.anchor}` : ''}`} onClick={onClick || undefined}>
      {inner}
    </LinkComponent>
  )
}

export const ensureLeadingSlash = (url: string) => (url.startsWith('/') ? url : '/' + url)

const possibleLanguagesInLink = ['en', 'nl']

export const removeLanguageFromLinkStart = (url: string) => {
  var split = ensureLeadingSlash(url).split('/').slice(1)
  if (possibleLanguagesInLink.findIndex(v => v == split[0]) == -1) return url
  return ensureLeadingSlash(split.slice(1).join('/'))
}
